import type { Aggreg8Bank, LeadWithAccountAnalysis } from "@bankmonitor/customer-portal-rest-client";
import { bankAnalysesToAuthorizations, bankNameLookup } from "./Analysis.mapper";
import type { Authorization } from "./AnalysisService.type";
import { accountAnalysisApi, accountSyncApi } from "../restClient";

// biome-ignore lint/complexity/noStaticOnlyClass: app-wide singleton cache for the lead and bank/analysis lists
export default class AccountAnalysisService {
	private static lead: LeadWithAccountAnalysis | null = null;
	private static banksPromise?: Promise<Aggreg8Bank[]>;
	private static analysisListPromise?: Promise<Authorization[]>;

	/** Fetch and cache the lead once at app startup. A failed fetch is treated as "feature disabled". */
	public static async init(): Promise<LeadWithAccountAnalysis | null> {
		try {
			AccountAnalysisService.lead = await accountAnalysisApi.getLeadWithRequestAccountAnalysis();
		} catch {
			AccountAnalysisService.lead = null;
		}

		return AccountAnalysisService.lead;
	}

	public static getLead(): LeadWithAccountAnalysis | null {
		return AccountAnalysisService.lead;
	}

	public static isEnabled(): boolean {
		return AccountAnalysisService.lead !== null;
	}

	public static getBanks(forceReload = false): Promise<Aggreg8Bank[]> {
		if (forceReload || AccountAnalysisService.banksPromise === undefined) {
			AccountAnalysisService.banksPromise = accountSyncApi.getAvailableBanks().catch((error: unknown) => {
				AccountAnalysisService.banksPromise = undefined;
				throw error;
			});
		}

		return AccountAnalysisService.banksPromise;
	}

	public static getAnalysisList(forceReload = false): Promise<Authorization[]> {
		if (forceReload || AccountAnalysisService.analysisListPromise === undefined) {
			// Best-effort bank names; on failure the cards fall back to the bank code.
			const banks = AccountAnalysisService.getBanks().catch(() => [] as Aggreg8Bank[]);

			AccountAnalysisService.analysisListPromise = Promise.all([accountAnalysisApi.getAccountAnalyses(), banks])
				.then(([analyses, loadedBanks]) => bankAnalysesToAuthorizations(analyses, bankNameLookup(loadedBanks)))
				.catch((error: unknown) => {
					AccountAnalysisService.analysisListPromise = undefined;
					throw error;
				});
		}

		return AccountAnalysisService.analysisListPromise;
	}

	public static invalidateBanks(): void {
		AccountAnalysisService.banksPromise = undefined;
	}

	public static invalidateAnalysisList(): void {
		AccountAnalysisService.analysisListPromise = undefined;
	}

	public static invalidateAll(): void {
		AccountAnalysisService.invalidateBanks();
		AccountAnalysisService.invalidateAnalysisList();
	}

	public static reset(): void {
		AccountAnalysisService.lead = null;
		AccountAnalysisService.invalidateAll();
	}
}
